<template>
  <div v-if="!loader">
    <b-card v-if="$store.state.app.language == 'en' && userRole === 'seller'">
      <h4 class="mb-2"><strong>Referral Program: Grow Together</strong></h4>
      <p>
        We are excited to introduce the new Aequitex Referral Program – our way of thanking you for your recommendations
        and for actively shaping the Aequitex community.
      </p>
      <p><strong>How it works:</strong></p>
      <p>
        As an existing Aequitex client, you can refer new businesses to join the platform as <strong>invoice sellers</strong>.
        When a referred business successfully registers, completes identity verification, and sells their first invoice on Aequitex, you benefit in the long term:
      </p>
      <p>
        👉 <strong>You will receive 20% of the fees</strong> Aequitex earns from the referred company's transactions on the marketplace.
        (You will receive any credit note at the end of each quarter)
      </p>
      <p><strong>Your benefits at a glance:</strong></p>
      <ul>
        <li>
          <strong>Ongoing earnings:</strong> As long as the referred company actively sells invoices on Aequitex, you will receive 20% of the selling fees.
        </li>
        <li>
          <strong>No limit:</strong> Refer as many new companies as you like and build a growing stream of passive income.
        </li>
        <li>
          <strong>Simple & transparent:</strong> All referrals are automatically tracked, attributed to your account, and your share is credited to your dashboard on a quarterly basis.
        </li>
      </ul>
      <p>
        Let’s grow together and share the success.<br/>
        Join our mission to offer SMEs fair, fast, and transparent access to liquidity – and help us take Aequitex to the next level.
      </p>
    </b-card>

    <b-card v-else-if="$store.state.app.language == 'de' && userRole === 'seller'">
      <h4 class="mb-2"><strong>Empfehlungsprogramm: Gemeinsam profitieren</strong></h4>
      <p>
        Wir freuen uns, Ihnen das neue Aequitex Empfehlungsprogramm vorstellen zu dürfen – unsere Art, uns für Ihre
        Weiterempfehlung und Ihre aktive Mitgestaltung der Aequitex-Community zu bedanken.
      </p>
      <p><strong>So funktioniert es:</strong></p>
      <p>
        Als bestehender Aequitex-Kunde haben Sie die Möglichkeit, neue Unternehmen als <strong>Rechnungsverkäufer</strong> für unsere
        Plattform zu gewinnen. Wenn sich ein von Ihnen geworbenes Unternehmen erfolgreich registriert, identifiziert und
        seine erste Rechnung über Aequitex verkauft, profitieren Sie langfristig:
      </p>
      <p>
        👉 <strong>Sie erhalten 20 % Anteil an den Gebühren</strong>, die Aequitex bei den Transaktionen des von Ihnen geworbenen Unternehmens über den Marktplatz erhebt. (Sie erhalten die etwaige Gutschrift am Ende eines jeden Quartals)
      </p>
      <p><strong>Ihre Vorteile auf einen Blick:</strong></p>
      <ul>
        <li>
          <strong>Langfristige Beteiligung:</strong> Solange das geworbene Unternehmen aktiv Rechnungen über Aequitex verkauft, erhalten Sie anteilig 20 % der Verkaufsgebühren.
        </li>
        <li>
          <strong>Keine Begrenzung:</strong> Sie können beliebig viele Neukunden werben und sich so ein wachsendes passives Einkommen sichern.
        </li>
        <li>
          <strong>Einfach & transparent:</strong> Alle Empfehlungen werden automatisch zugeordnet, getrackt und Ihr Anteil wird Ihrem Dashboard vierteljährlich gutgeschrieben.
        </li>
      </ul>
      <p>
        Gemeinsam wachsen – gemeinsam profitieren.<br/>
        Werden Sie Teil unserer Mission, KMUs einen fairen, schnellen und transparenten Zugang zu Liquidität zu ermöglichen – und gestalten Sie den Erfolg von Aequitex aktiv mit.
      </p>
    </b-card>

    <b-card v-else-if="$store.state.app.language == 'en' && userRole === 'buyer'">
      <h4 class="mb-2"><strong>Referral Program for Investors: Your Network – Your Benefit</strong></h4>
      <p>
        As an active investor on the Aequitex platform, you now have the opportunity to shape the growth of our community –
        while earning ongoing rewards.
      </p>
      <p><strong>How it works:</strong></p>
      <p>
        Refer new users to the Aequitex platform – whether they are <strong>invoice sellers</strong> or other
        <strong>investors</strong>. Once a referred user has successfully registered and completed their first transaction,
        you receive:
      </p>
      <p>👉 <strong>20% of the fees</strong> Aequitex earns from the referred user's marketplace transactions.</p>
      <p><strong>Your benefits:</strong></p>
      <ul>
        <li>
          <strong>Flexible referral options:</strong> Invite both invoice sellers and new investors (invoice buyers).
        </li>
        <li>
          <strong>Ongoing Revenue:</strong> The commission permanently applies to the transaction fees (invoice purchase/sale) generated via the marketplace by the referred users. <i>(as long as your Aequitex account remains active)</i>
        </li>
        <li>
          <strong>Effortless sharing:</strong> All referrals are automatically tracked, attributed to your account and your
          share is credited to your dashboard on a quarterly basis.
        </li>
      </ul>
      <p>Support growth, enhance liquidity and share in the success.</p>
    </b-card>

    <b-card v-else-if="$store.state.app.language == 'de' && userRole === 'buyer'">
      <h4 class="mb-2"><strong>Empfehlungsprogramm für Investoren: Ihre Kontakte – Ihr Vorteil</strong></h4>
      <p>
        Als aktiver Investor auf der Aequitex-Plattform haben Sie jetzt die Möglichkeit, das Wachstum der Community aktiv
        mitzugestalten – und dabei direkt zu profitieren.
      </p>
      <p><strong>So funktioniert es:</strong></p>
      <p>
        Empfehlen Sie neue Nutzer für die Aequitex-Plattform – egal ob als <strong>Rechnungsverkäufer</strong> oder als
        weitere <strong>Investoren</strong> (Rechnungskäufer). Sobald ein von Ihnen geworbener Nutzer erfolgreich
        registriert ist und seine erste Transaktion über die Plattform abgeschlossen hat, erhalten Sie:
      </p>
      <p>
        👉 <strong>20 % Anteil an den Gebühren</strong>, die Aequitex auf Marktplatz-Transaktionen des geworbenen Nutzers erhebt.
        (Sie erhalten die etwaige Gutschrift am Ende eines jeden Quartals)
      </p>
      <p><strong>Ihre Vorteile:</strong></p>
      <ul>
        <li>
          <strong>Breites Empfehlungsmodell:</strong> Werben Sie sowohl Rechnungsverkäufer als auch neue
          Rechnungskäufer (Investoren).
        </li>
        <li>
          <strong>Laufende Einnahmen:</strong> Die Provision gilt dauerhaft auf die Transaktionsgebühren (Rechnungsver-/-ankauf) über den Marktplatz der geworbenen Nutzer. <i>(solange Ihr Aequitex-Account aktiv ist)</i>
        </li>
        <li>
          <strong>Einfach in der Umsetzung:</strong> Empfehlungen werden automatisch zugeordnet, getrackt und Ihr Anteil
          wird Ihrem Dashboard vierteljährlich gutgeschrieben.
        </li>
      </ul>
      <p>Unterstützen Sie Wachstum, fördern Sie Liquidität und profitieren Sie gemeinsam mit uns.</p>
    </b-card>

    <b-card v-if="$store.state.app.language == 'en' && userRole === 'partner'">
      <h4 class="mb-2">
        <strong>Partner-Program: Build Value, Earn More</strong>
      </h4>
      <p>
        Welcome to the Aequitex Partner-Program — designed for strategic partners who help bring high-quality businesses into the Aequitex ecosystem.
      </p>

      <p>
        <strong>How it works:</strong>
      </p>
      <p>
        As an Aequitex partner, you can introduce new companies to the platform as either
        <strong>invoice sellers</strong> or <strong>investors</strong>. Once your referred business completes registration, verification, and performs their first transaction, you unlock ongoing earnings:
      </p>

      <p v-if="privileges && privileges.referral_commission">
        👉 <strong>You earn {{ privileges.referral_commission }}% of the fees</strong> generated by your referred companies’ marketplace activity. (Non-cumulative, if both parties were referred by you  - any credit note is paid out at the end of each quarter)
      </p>

      <p v-if="privileges && privileges.share_from_annual_fee">
        👉 <strong>You earn {{ privileges.share_from_annual_fee }}% of the first annual fees</strong> generated by your referred seller companies’ marketplace activity.
      </p>

      <p>
        👉 <strong>Your referred seller company gets the first annual fee waived</strong> (Any credit note is paid out at the end of each quarter)
      </p>

      <p><strong>Your advantages at a glance:</strong></p>
      <ul>
        <li>
          <strong>Scalable revenue stream:</strong> As long as referred companies stay active, your commission applies to their transaction fees.
        </li>
        <li>
          <strong>No limits:</strong> Onboard as many sellers or investors as you’d like — your earning potential scales infinitely.
        </li>
        <li>
          <strong>Seamless tracking:</strong> All referrals are automatically assigned to your partner account and updated quarterly.
        </li>
      </ul>

      <p>
        Expand your network, elevate liquidity, and grow with us — as an Aequitex partner.
      </p>
    </b-card>

    <b-card v-else-if="$store.state.app.language == 'de' && userRole === 'partner'">
      <h4 class="mb-2">
        <strong>Partner-Programm: Ihr Netzwerk – Ihre Vorteile</strong>
      </h4>
      <p>
        Willkommen beim Aequitex Partner-Programm — entwickelt für strategische Partner, die hochwertige Unternehmen in das Aequitex-Ökosystem einbringen.
      </p>

      <p>
        <strong>So funktioniert es:</strong>
      </p>
      <p>
        Als Aequitex-Partner können Sie neue Unternehmen auf die Plattform bringen – entweder als
        <strong>Rechnungsverkäufer</strong> oder <strong>Investoren</strong>. Sobald das von Ihnen empfohlene Unternehmen die Registrierung, Verifizierung und seine erste Transaktion abgeschlossen hat, werden Ihre fortlaufenden Erträge aktiviert:
      </p>

      <p>
        👉 <strong>Sie verdienen {{ privileges.referral_commission }}% der Gebühren</strong>, die durch die Marktplatzaktivität Ihrer empfohlenen Unternehmen generiert werden. (Nicht kumulativ, falls beide Parteien von Ihnen empfohlen wurden – jede Gutschrift wird am Ende jedes Quartals ausbezahlt.)
      </p>
      <p>
        👉 <strong>Sie verdienen {{ privileges.share_from_annual_fee }}% der ersten Jahresgebühren</strong>, die durch die Marktplatzaktivität Ihrer empfohlenen Verkäufer generiert werden.
      </p>
      <p>
        👉 <strong>Ihrem empfohlenen Verkäufer wird die erste Jahresgebühr erlassen</strong>. (Jede Gutschrift wird am Ende jedes Quartals ausbezahlt.)
      </p>

      <p><strong>Ihre Vorteile auf einen Blick:</strong></p>
      <ul>
        <li>
          <strong>Skalierbare Einnahmen:</strong> Solange die empfohlenen Unternehmen aktiv bleiben, erhalten Sie Kommissionen auf deren Transaktionsgebühren.
        </li>
        <li>
          <strong>Keine Grenzen:</strong> Onboarden Sie beliebig viele Verkäufer oder Investoren — Ihr Verdienstpotenzial wächst unbegrenzt.
        </li>
        <li>
          <strong>Nahtloses Tracking:</strong> Alle Empfehlungen werden automatisch Ihrem Partnerkonto zugeordnet und quartalsweise aktualisiert.
        </li>
      </ul>

      <p>
        Erweitern Sie Ihr Netzwerk, stärken Sie die Liquidität und wachsen Sie mit uns — als Aequitex-Partner.
      </p>
    </b-card>

    <b-card class="p-2 shadow-sm">
      <b-row>
        <b-col cols="12" md="6">
          <!-- Header Row -->
          <b-row v-if="code == null" class="align-items-center mb-2">
            <b-col>
              <b-button
                  variant="primary"
                  size="md"
                  v-if="code == null"
                  @click="generateLink"
                >
                {{ $t("generate_link") }}
              </b-button>
            </b-col>
          </b-row>

          <!-- Referral Link -->
          <b-row class="align-items-center mb-1">
            <b-col class="col-md-8">
              <label class="font-weight-bold mb-0 size"><strong>{{ $t('code') }}</strong></label>
              <b-input-group>
                <b-form-input
                    readonly
                    :value="referalData ? referalData.rrp_link : code"
                    class="bg-light code-input"
                ></b-form-input>
                <b-input-group-append>
                  <b-button variant="outline-secondary" size="sm" @click="copyToClipboard" id="copyButton">
                    <feather-icon icon="CopyIcon" size="14" />
                  </b-button>
                  <b-tooltip target="copyButton" triggers="click" :show.sync="showCopiedTooltip" placement="top">
                    Copied!
                  </b-tooltip>
                </b-input-group-append>
              </b-input-group>
              <small class="text-muted">{{ $t("copy_and_share_link") }}</small>
            </b-col>
          </b-row>

          <!-- Statistics -->
          <b-row>
            <b-col cols="6">
              <strong>{{ $t("registered_companies") }}</strong>:
              <span class="ml-1">{{ companies ? companies.length : '0' }}</span>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-card>


    <b-card v-if="companies">
      <b-table hover :items="companies" :fields="fields" responsive class="centered-table">

        <template #head()="scope">
          <div class="text-nowrap">{{ $t(scope.label) }}</div>
        </template>

        <template #cell(name)="data">
          <span>{{ data.value ? data.value : '-' }}</span>
        </template>

        <template #cell(user_name)="data">
          <span  class="badge badge-light-secondary">{{ data.item.main_contact_name }}</span>
        </template>

        <template #cell(invoices_sold)="data">
          <span>{{ data.value }}</span>
        </template>

        <template class="d-block text-center" #cell(company_name)="data">
          <span class="d-block text-center">{{ data.value ? data.value : '-' }}</span>
        </template>

        <template #cell(verified)="data">
          <span v-if="data.item.verified == 0">{{ $t("referal_clicked_link_not_verified") }}</span>
          <span v-if="data.item.verified == 1 && data.item.invoices_sold == 0">{{ $t("referal_vierified_no_invoices") }}</span>
          <span v-if="data.item.verified == 1 && data.item.invoices_sold > 0">{{ $t("recommendation_successful") }}</span>
        </template>
      </b-table>
    </b-card>
  </div>

  <div v-else><img src="/new-loader.svg"/></div>
</template>
<script>
import {ValidationProvider, ValidationObserver} from "vee-validate";
import ToastificationContent from "@core/components/toastification/ToastificationContent";

export default {
  name: 'referralProgram',
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      billing: null,
      company: null,
      privileges: null,
      loader: false,
      preregistered: false,
      referalData: null,
      companies: [],
      code: null,
      fields: [
        {
          label: "user_name",
          align: "start",
          key: "user_name",
        },
        {
          label: "company_name",
          align: "start",
          key: "company_name",
        },
        {
          label: "status",
          align: "start",
          key: "verified",
        },
      ],
      showCopiedTooltip: false,
    }
  },
  computed: {
    userRole() {
      return this.$store.state.auth.user.crole
    }
  },
  created() {
    this.getInfo();
    this.getCode();
    this.getPartnerPrivileges();
    this.getCompaniesList();
    this.getBilling();
    this.getBilling();
  },
  methods: {
    getInfo() {
      this.loader = true;
      this.$http
      .post(`/company/show/${this.$store.state.auth.user.company_id}`)
      .then((res) => {
        if (res) {
          this.loader = false;
          this.company = res.data.value;
          this.$http
          .post("/get-company-data-from-zefix", {
            search: res.data.value.uid
          })
          .then((res) => {
            if (res.data.value) {
             this.preregistered = res.data.value.preregistered;
            }
          });
        }
      });
    },
    getCode() {
      this.$http
      .post(`/company/referral/getCode`)
      .then((res) => {
        if (res && res.data && res.data.value) {
          this.code = res.data.value.code;
        }
      });
    },
    getPartnerPrivileges() {
      this.$http.post(`/company/referral/getPrivileges`).then((res) => {
        if (res && res.data && res.data.value) {
          this.privileges = res.data.value;
        }
      });
    },
    generateLink() {
      this.$http
      .post(`/company/referral/generateRRP`)
      .then((res) => {
        if (res) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Success",
              icon: "SuccessIcon",
              variant: "success",
            },
          });
          this.referalData = res.data.value;
          this.code = res.data.value.code || true;
        }
      });
    },
    getCompaniesList() {
      this.$http
      .post(`/company/referral/getCompanies`)
      .then((res) => {
        if (res) {
          this.companies = res.data.value;
        }
      });
    },
    getBilling() {
      this.$http
      .post(`/company/billing/get`, {company_id: this.$store.state.auth.user.company_id})
      .then((res) => {
        if (res) {
          this.billing = res.data.value[0];
        }
      });
    },
    copyToClipboard() {
      const codeToCopy = this.referalData ? this.referalData.rrp_link : this.code;
      const userRole = this.$store.state.auth.user.crole;
      const encodedRole = btoa(userRole);
      const fullLink = `${window.location.origin}/register?affiliate_code=${codeToCopy}&referrer_role=${encodedRole}`;
      navigator.clipboard.writeText(fullLink).then(() => {
        this.showCopiedTooltip = true;
        setTimeout(() => {
          this.showCopiedTooltip = false;
        }, 1000);
      }).catch(err => {
        console.error("Failed to copy to clipboard", err);
      });
    },
  }
};
</script>

<style>
.centered-table td,
.centered-table th {
  text-align: center;
  vertical-align: middle;
}
</style>
